import React, { useEffect, useRef, useState } from 'react';

// Component specifically designed to handle embossed item rendering with stable scaling
const EmbossedItemRenderer = ({ item, canvasWidth, canvasHeight, gridSize }) => {
  const imgRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const isMobile = viewportWidth < 768;
  
  // Parse grid dimensions for dynamic scaling
  const getGridDimensions = () => {
    if (!gridSize || typeof gridSize !== 'string') return { cols: 6, rows: 6 }; // Default
    
    const parts = gridSize.split('x');
    if (parts.length !== 2) return { cols: 6, rows: 6 }; // Default if invalid format
    
    const cols = parseInt(parts[0], 10) || 6;
    const rows = parseInt(parts[1], 10) || 6;
    return { cols, rows };
  };
  
  const { cols, rows } = getGridDimensions();

  // Listen for viewport size changes to adjust sizing for mobile
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate the size to ensure consistent display across devices
  const calculateSize = () => {
    // IMPROVED: Set minimum base size and dynamically scale it based on grid dimensions
    // For small grids, we want a larger base size (e.g., 400 for 4x4)
    // For larger grids, we'll gradually decrease the base size
    let baseSize;
    
    // Apply user's scale or use appropriate default if not set yet
    const userScale = item.scale !== undefined ? item.scale : getDefaultScaleForGridSize(cols, rows);
    
    // Determine appropriate base size based on grid dimensions
    const maxDimension = Math.max(cols, rows);
    
    if (maxDimension <= 4) {
      // Very small grids (4x4) get a very large base size
      baseSize = 400;
    } else if (maxDimension <= 6) {
      // Small grids (5x5, 6x6) get a large base size
      baseSize = 300;
    } else if (maxDimension <= 8) {
      // Medium grids (7x7, 8x8) get a medium base size
      baseSize = 200;
    } else if (maxDimension <= 10) {
      // Medium-large grids (9x9, 10x10) get a medium-small base size
      baseSize = 150;
    } else {
      // Large grids (11+) get a small base size
      baseSize = 120;
    }
    
    // Apply user's scale factor to the dynamically determined base size
    // IMPORTANT: Remove any scale capping/limiting here to allow values > 3
    const scaledBaseSize = baseSize * userScale;
    
    // Calculate a grid adjustment factor for fine-tuning
    // For very small grids, we make the adjustment factor larger
    let gridSizeAdjustment;
    
    if (maxDimension <= 4) {
      gridSizeAdjustment = 2.0; // Boost very small grids significantly more
    } else if (maxDimension <= 6) {
      gridSizeAdjustment = 1.5; // Boost small grids significantly
    } else if (maxDimension <= 8) {
      gridSizeAdjustment = 1.2; // Boost medium grids moderately
    } else if (maxDimension <= 10) {
      gridSizeAdjustment = 1.0; // No adjustment for medium-large grids
    } else {
      gridSizeAdjustment = 0.8; // Reduce more for large grids
    }
    
    // For uploaded embossed items, we want to maintain consistent sizing
    // regardless of device type to ensure what users see is what they get
    if (item.isUploaded) {
      // Use a consistent relative size system that works across all devices
      // vmin ensures size is relative to viewport's smallest dimension
      // Apply the grid size adjustment to make items proportionally sized based on grid size
      const sizePercentage = (scaledBaseSize / 400) * 100 * gridSizeAdjustment;
      
      return {
        width: `${sizePercentage}vmin`, // Use vmin for consistency
        height: 'auto',
        sizePercentage: sizePercentage,
        mode: 'consistent',
        gridSizeAdjustment: gridSizeAdjustment, // Store for debugging
        baseSize: baseSize // Store for debugging
      };
    } 
    // For sample items or other embossed elements, use device-specific sizing
    else {
      // On mobile, use viewport-relative sizing
      if (isMobile) {
        const sizePercentage = (scaledBaseSize / 400) * 100;
        // IMPORTANT: Remove min() function that might be capping the size
        const adjustedPercentage = sizePercentage * 1.2 * gridSizeAdjustment;
        
        return {
          width: `${adjustedPercentage}vmin`,
          height: 'auto',
          sizePercentage: adjustedPercentage,
          mode: 'mobile',
          gridSizeAdjustment: gridSizeAdjustment, // Store for debugging
          baseSize: baseSize // Store for debugging
        };
      } 
      // On desktop, use direct pixel values
      else {
        // Apply grid size adjustment to desktop pixel values too
        const adjustedSize = scaledBaseSize * gridSizeAdjustment;
        return {
          width: `${adjustedSize}px`,
          height: 'auto',
          sizePercentage: (adjustedSize / 400) * 100, // Store percentage for reference
          mode: 'desktop',
          gridSizeAdjustment: gridSizeAdjustment, // Store for debugging
          baseSize: baseSize // Store for debugging
        };
      }
    }
  };
  
  const sizingInfo = calculateSize();
  
  return (
    <div 
      className="embossed-item absolute pointer-events-none"
      style={{
        left: `${item.x}%`,
        top: `${item.y}%`,
        transform: `translate(-50%, -50%) rotate(${item.rotation || 0}deg)`,
        zIndex: item.zIndex || 10,
        opacity: item.opacity || 1
      }}
      data-is-mobile={isMobile}
      data-original-width={item.width}
      data-original-scale={item.scale}
      data-fixed-size={item.fixedSize}
      data-size-percentage={sizingInfo.sizePercentage}
      data-sizing-mode={sizingInfo.mode}
      data-is-uploaded={item.isUploaded ? 'true' : 'false'}
      data-grid-size={gridSize}
      data-grid-cols={cols}
      data-grid-rows={rows}
      data-grid-adjustment={sizingInfo.gridSizeAdjustment}
      data-base-size={sizingInfo.baseSize}
    >
      <img
        ref={imgRef}
        src={item.dataUrl}
        alt="Embossed element"
        className="w-full h-full object-contain"
        style={{
          width: sizingInfo.width,
          height: sizingInfo.height,
          maxWidth: 'none', // Prevent responsive scaling
          display: 'block', // Remove inline spacing
          transformOrigin: 'center center',
          willChange: 'transform',
          objectFit: 'contain',
          zIndex: item.zIndex || 20,
          filter: `drop-shadow(0 2px 3px rgba(0,0,0,0.2))`
        }}
        onError={(e) => {
          console.error("Failed to load embossed item image:", e);
          e.target.style.backgroundColor = "rgba(255,0,0,0.2)";
          e.target.style.width = "50px";
          e.target.style.height = "50px";
        }}
      />
    </div>
  );
};

// Utility function to get default scale based on grid size
// Export this so it can be used across components
export const getDefaultScaleForGridSize = (cols, rows) => {
  // Always return 2.0 regardless of grid size
  return 2.0;
};

export default EmbossedItemRenderer;
